import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Translate, withLocalize } from 'react-localize-redux';
import Table from '../../../../components/organisms/Table';
import UnderRow from './TableComponents/UnderRow';
import {
    DNS_ZONE,
    DOMAIN_TYPE_OPTIONS,
    DOMAIN_TYPES,
    STATUSES,
    TABLE_KEYS,
    TTypeEnum,
    VALIDATION_MESSAGES,
} from './consts';
import { getAlertButtonMessage, getAlertMessage, tableColumns } from './tableUtils';
import css from './SubDomainLinkTable.css';
import Icon from '../../../../components/foundations/Icon';
import { getUserData } from '../../../../selectors/user';
import UnderRowLogic from './TableComponents/UnderRowLogic';
import { getCNAMEFromDomain } from './utils';

const SUBDOMAIN_REGEX = /^(?![^-\n]*(?:-(?!\.)[^-\n]*)*-\.)(?:(?!-)[A-Za-z0-9-]{1,63})$/;
const BRANDED_DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

const SubDomainLinkTable = ({
    subDomains = [],
    brandedDomainsLimit,
    translate,
    isCreatingDomain = false,
    onCreateClicked = () => {},
    onCreateLinksDomainClick = () => {},
    onArchiveClicked = () => {},
    onFavouriteClicked = () => {},
    onCancelCreateDomain = () => {},
    domainsLimit = 50,
    isAgency = false,
    subDomainsPage,
}) => {
    const [newSubdomain, setNewSubdomain] = useState('');
    const [cNameErrors, setCNameErrors] = useState({});
    const [domainType, setDomainType] = useState(DOMAIN_TYPE_OPTIONS[0]);
    const [validationError, setValidationError] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [wasWorking, setWasWorking] = useState(false);
    const { is_paying_customer: isPayingOrg, is_org_admin: isAdmin } = useSelector(getUserData);
    const brandedDomains = subDomains.filter(e => e.zone !== DNS_ZONE);
    const subDomainsCount = subDomains.length - brandedDomains.length;

    const isValidFormat = useCallback((name, isBrandedDomain) => {
        const trimmedName = name?.trim();
        if (!trimmedName) return false;
        return isBrandedDomain ? BRANDED_DOMAIN_REGEX.test(trimmedName) : SUBDOMAIN_REGEX.test(trimmedName);
    }, []);

    const isDuplicate = useCallback(
        (name, isBrandedDomain) => {
            if (isBrandedDomain) {
                return subDomains.some(domain => domain.fullDomain.toLowerCase() === name?.toLowerCase());
            }
            return subDomains.some(
                domain => domain.subdomain.toLowerCase() === name?.toLowerCase() && domain.zone === DNS_ZONE
            );
        },
        [subDomains]
    );

    const validate = useCallback(
        (name, isBranded) => {
            const trimmed = name.trim();
            if (!trimmed) return '';
            if (!isValidFormat(trimmed, isBranded)) return translate(VALIDATION_MESSAGES.INVALID_FORMAT);
            if (isDuplicate(trimmed, isBranded)) return translate(VALIDATION_MESSAGES.DUPLICATE);
            return '';
        },
        [isValidFormat, isDuplicate, translate]
    );

    useEffect(() => {
        const { working: currentWorking, error: currentError } = subDomainsPage || {};

        if (currentWorking !== wasWorking) {
            setWasWorking(currentWorking);
        }

        if (isCreating && wasWorking && !currentWorking) {
            if (!currentError) {
                setNewSubdomain('');
                setValidationError('');
                setDomainType(DOMAIN_TYPE_OPTIONS[0]);
            }
            setIsCreating(false);
            setWasWorking(false);
        }
    }, [subDomainsPage?.working, subDomainsPage?.error, isCreating, wasWorking]);

    const normalizedRows = useMemo(
        () =>
            subDomains.map(subdomain => {
                const isBrandedDomain = subdomain.zone !== DNS_ZONE;
                const domainKey = subdomain.fullDomain;
                const hasCNameError =
                    isBrandedDomain && subdomain.status === STATUSES.PENDING_CNAME && cNameErrors[domainKey];

                return {
                    [TABLE_KEYS.DOMAIN_TYPE]: isBrandedDomain ? DOMAIN_TYPES.BRANDED : DOMAIN_TYPES.SINGULAR,
                    [TABLE_KEYS.DOMAIN]: subdomain.fullDomain,
                    [TABLE_KEYS.STATUS]: subdomain.status,
                    isFavourite: Boolean(subdomain.favourite),
                    zone: subdomain.zone,
                    subdomain: subdomain.subdomain,
                    targetCNAME: subdomain.targetCNAME,
                    isCreatingDomain: false,
                    isPayingOrg,
                    isBrandedDomain,
                    isCNameError: hasCNameError,
                    underRowComponent: () =>
                        isBrandedDomain && subdomain.status !== STATUSES.LIVE ? (
                            <UnderRowLogic
                                isCNameError={hasCNameError}
                                domain={subdomain.fullDomain}
                                status={subdomain.status}
                            />
                        ) : null,
                };
            }),
        [subDomains, isPayingOrg, cNameErrors]
    );

    const newDomainRow = useMemo(() => {
        const isBrandedDomain = domainType.display_name === DOMAIN_TYPES.BRANDED;
        const isDuplicateSubdomain = isDuplicate(newSubdomain, isBrandedDomain);
        const isValidSubdomain = isValidFormat(newSubdomain, isBrandedDomain);
        const rowDisabled =
            isCreatingDomain &&
            (isBrandedDomain ? brandedDomains.length >= brandedDomainsLimit : subDomainsCount >= domainsLimit);

        const getRowStatus = () => {
            if (!isCreatingDomain) return STATUSES.LIVE;
            if (!isPayingOrg && (subDomainsCount >= domainsLimit || isBrandedDomain)) return STATUSES.UPGRADE;
            if (
                (!isBrandedDomain && subDomainsCount >= domainsLimit) ||
                (isBrandedDomain && brandedDomains.length >= brandedDomainsLimit)
            )
                return STATUSES.LIMIT_REACHED;
            return STATUSES.EDIT;
        };

        return {
            [TABLE_KEYS.DOMAIN_TYPE]: DOMAIN_TYPES.SINGULAR,
            [TABLE_KEYS.DOMAIN]: '',
            [TABLE_KEYS.STATUS]: getRowStatus(),
            isFavourite: false,
            subdomain: newSubdomain,
            isCreatingDomain: true,
            isLoading: Boolean(subDomainsPage?.working),
            isBrandedDomain,
            isCreatingButtonDisabled: isDuplicateSubdomain || !isValidSubdomain,
            isRowDisabled: rowDisabled,
            underRowComponent: () =>
                rowDisabled ? (
                    <UnderRow
                        actionButtonText={getAlertButtonMessage(isPayingOrg, isBrandedDomain)}
                        type={TTypeEnum.ALERT}
                        message={getAlertMessage(
                            subDomains,
                            domainsLimit,
                            isPayingOrg,
                            brandedDomainsLimit,
                            isBrandedDomain,
                            isAdmin
                        )}
                    />
                ) : null,
        };
    }, [
        newSubdomain,
        isDuplicate,
        isValidFormat,
        isPayingOrg,
        domainType,
        brandedDomains,
        subDomainsPage?.working,
        domainsLimit,
        isCreatingDomain,
        brandedDomainsLimit,
        isAdmin,
        subDomains,
        subDomainsCount,
    ]);

    const tableData = useMemo(() => (isCreatingDomain ? [newDomainRow, ...normalizedRows] : normalizedRows), [
        isCreatingDomain,
        newDomainRow,
        normalizedRows,
    ]);

    const handleSubdomainChange = useCallback(
        value => {
            setNewSubdomain(value);
            const isBrandedDomain = domainType.display_name === DOMAIN_TYPES.BRANDED;
            setValidationError(validate(value, isBrandedDomain));
        },
        [validate, domainType]
    );

    const handleCreateClick = useCallback(
        async row => {
            if (row.isBrandedDomain && row.status === STATUSES.PENDING_CNAME) {
                const domainKey = row.domain;
                const targetCNAME = await getCNAMEFromDomain(row.domain);

                setCNameErrors(prev => ({
                    ...prev,
                    [domainKey]: !targetCNAME,
                }));

                if (targetCNAME) onCreateClicked(row.subdomain, row.zone, targetCNAME);
                return;
            }

            if (!isCreatingDomain && !row.isBrandedDomain && row.status !== STATUSES.PENDING_CNAME) return;

            const trimmedInput = row.isBrandedDomain ? row.subdomain : newSubdomain.trim();

            if (!trimmedInput) {
                setValidationError(translate(VALIDATION_MESSAGES.REQUIRED));
                return;
            }

            const isBrandedDomain = domainType.display_name === DOMAIN_TYPES.BRANDED || row.isBrandedDomain;
            const [subdomain, dnsZone] = isBrandedDomain
                ? (() => {
                      const dotIndex = trimmedInput.indexOf('.');
                      return [trimmedInput.slice(0, dotIndex), trimmedInput.slice(dotIndex + 1)];
                  })()
                : [trimmedInput, DNS_ZONE];

            const error = validate(trimmedInput, isBrandedDomain);
            if (error) {
                setValidationError(error);
                return;
            }

            setIsCreating(true);
            setWasWorking(false);
            onCreateClicked(subdomain, dnsZone, isBrandedDomain ? row.targetCNAME : null);
        },
        [isCreatingDomain, newSubdomain, validate, onCreateClicked, translate, domainType]
    );

    const changeDomainType = useCallback(newType => {
        setNewSubdomain('');
        setDomainType(newType);
    }, []);

    const handleCancelCreate = useCallback(() => {
        setNewSubdomain('');
        setValidationError('');
        setIsCreating(false);
        setWasWorking(false);
        setCNameErrors({});
        onCancelCreateDomain();
    }, [onCancelCreateDomain]);

    const tableMetadata = useMemo(
        () =>
            tableColumns(
                onArchiveClicked,
                onFavouriteClicked,
                handleCreateClick,
                handleCancelCreate,
                handleSubdomainChange,
                changeDomainType,
                validationError,
                isAgency,
                subDomainsPage,
                translate
            ),
        [
            onArchiveClicked,
            onFavouriteClicked,
            handleCreateClick,
            handleCancelCreate,
            handleSubdomainChange,
            changeDomainType,
            validationError,
            isAgency,
            subDomainsPage,
            translate,
        ]
    );

    return (
        <>
            <Table
                tableClass={css.tableClass}
                showInnerBorder={false}
                trClass={css.trCell}
                thClass={css.thClass}
                zebraTable={false}
                data={tableData}
                metadata={tableMetadata}
            />
            {!tableData.length && (
                <div className={css.emptyContainer}>
                    <Icon onClick={onCreateLinksDomainClick} className={css.icon} name="link-plus" />
                    <Translate id="STATIC.PAGES.MANAGE_LINKS.SUBDOMAIN_SHELF_EMPTY" />
                </div>
            )}
        </>
    );
};

SubDomainLinkTable.propTypes = {
    subDomains: PropTypes.arrayOf(
        PropTypes.shape({
            favourite: PropTypes.bool,
            zone: PropTypes.string,
            fullDomain: PropTypes.string,
            subdomain: PropTypes.string,
        })
    ),
    translate: PropTypes.func.isRequired,
    isCreatingDomain: PropTypes.bool,
    onCreateClicked: PropTypes.func,
    onCreateLinksDomainClick: PropTypes.func,
    onArchiveClicked: PropTypes.func,
    onFavouriteClicked: PropTypes.func,
    onCancelCreateDomain: PropTypes.func,
    domainsLimit: PropTypes.number,
    brandedDomainsLimit: PropTypes.number,
    isAgency: PropTypes.bool,
    subDomainsPage: PropTypes.object,
};

export default withLocalize(SubDomainLinkTable);
